.page {
  /* Colors */
  --gh-bg: #0d1117;
  --gh-bg-nav: #010409;
  --gh-text: #e6edf3;
  --gh-text-bright: #f0f6fc;
  --gh-text-muted: #7d8590;
  --gh-border: #30363d;
  --gh-accent-orange: #f78166;
  --gh-accent-blue: #58a6ff;
  --gh-graph-0: #161b22;
  --gh-graph-1: #0e4429;
  --gh-graph-2: #006d32;
  --gh-graph-3: #26a641;
  --gh-graph-4: #39d353;

  /* Font sizes */
  --gh-font-2xs: 10px;
  --gh-font-xs: 11px;
  --gh-font-sm: 12px;
  --gh-font-base: 13px;
  --gh-font-md: 15px;
  --gh-font-lg: 18px;

  /* Sizing */
  --gh-icon-sm: 10px;
  --gh-icon-md: 12px;
  --gh-icon-lg: 20px;
  --gh-avatar-max: 120px;
  --gh-search-max: 200px;
  --gh-graph-cell-size: 8px;
  --gh-lang-dot-size: 8px;

  /* Border radius */
  --gh-radius: 6px;
  --gh-graph-cell-radius: 1px;

  background: var(--gh-bg);
  color: var(--gh-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: var(--gh-font-base);
  min-height: 100%;
}

/* Nav */
.nav {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: var(--gh-bg-nav);
  border-bottom: 1px solid var(--gh-border);
}

.navLogo {
  display: flex;
  color: var(--gh-text-bright);
}

.navLogo svg {
  width: var(--gh-icon-lg);
  height: var(--gh-icon-lg);
}

.navSearch {
  padding: 3px 8px;
  border: 1px solid var(--gh-border);
  border-radius: var(--gh-radius);
  color: var(--gh-text-muted);
  font-size: var(--gh-font-sm);
  flex: 1;
  max-width: var(--gh-search-max);
}

/* Profile layout */
.profileLayout {
  display: flex;
  gap: 20px;
  padding: 16px;
  max-width: 100%;
}

/* Sidebar */
.sidebar {
  flex: 0 0 30%;
  min-width: 0;
}

.avatarWrap {
  position: relative;
  width: 100%;
  max-width: var(--gh-avatar-max);
  aspect-ratio: 1;
  margin-bottom: 8px;
  background: var(--gh-bg);
  border-radius: 50%;
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid var(--gh-border);
  object-fit: cover;
}

.displayName {
  font-size: var(--gh-font-lg);
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
}

.username {
  font-size: var(--gh-font-md);
  color: var(--gh-text-muted);
  margin: 0 0 8px;
  font-weight: 300;
}

.bio {
  color: var(--gh-text);
  margin: 0 0 12px;
  line-height: 1.5;
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metaItem {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--gh-text-muted);
  font-size: var(--gh-font-sm);
}

.metaItem svg {
  width: var(--gh-icon-md);
  height: var(--gh-icon-md);
  flex-shrink: 0;
}

.metaItem strong {
  color: var(--gh-text);
}

/* Content */
.content {
  flex: 1;
  min-width: 0;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--gh-border);
  margin-bottom: 12px;
}

.tab {
  padding: 6px 10px;
  color: var(--gh-text);
  font-size: var(--gh-font-sm);
  cursor: default;
}

.tab:first-child {
  border-bottom: 2px solid var(--gh-accent-orange);
  font-weight: 600;
}

.sectionTitle {
  font-size: var(--gh-font-base);
  font-weight: 600;
  margin: 0 0 8px;
}

/* Pinned repos */
.pinnedGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.repoCard {
  border: 1px solid var(--gh-border);
  border-radius: var(--gh-radius);
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.repoName {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--gh-accent-blue);
  font-weight: 600;
  font-size: var(--gh-font-sm);
}

.repoName svg {
  width: var(--gh-icon-md);
  height: var(--gh-icon-md);
  color: var(--gh-text-muted);
  flex-shrink: 0;
}

.repoDesc {
  color: var(--gh-text-muted);
  font-size: var(--gh-font-xs);
  margin: 0;
  line-height: 1.4;
  flex: 1;
}

.repoMeta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
}

.repoLang {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--gh-font-xs);
  color: var(--gh-text-muted);
}

.langDot {
  width: var(--gh-lang-dot-size);
  height: var(--gh-lang-dot-size);
  border-radius: 50%;
  flex-shrink: 0;
}

.repoStars {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: var(--gh-font-xs);
  color: var(--gh-text-muted);
}

.repoStars svg {
  width: var(--gh-icon-sm);
  height: var(--gh-icon-sm);
}

/* Contribution graph */
.contributionGraph {
  border: 1px solid var(--gh-border);
  border-radius: var(--gh-radius);
  padding: 10px;
}

.contributionLabel {
  display: block;
  font-size: var(--gh-font-sm);
  color: var(--gh-text);
  margin-bottom: 8px;
}

.graphGrid {
  display: flex;
  gap: 2px;
  justify-content: center;
}

.graphColumn {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.graphCell {
  width: var(--gh-graph-cell-size);
  height: var(--gh-graph-cell-size);
  border-radius: var(--gh-graph-cell-radius);
}

.graphCell[data-level="0"] {
  background: var(--gh-graph-0);
}

.graphCell[data-level="1"] {
  background: var(--gh-graph-1);
}

.graphCell[data-level="2"] {
  background: var(--gh-graph-2);
}

.graphCell[data-level="3"] {
  background: var(--gh-graph-3);
}

.graphCell[data-level="4"] {
  background: var(--gh-graph-4);
}

.graphLegend {
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: flex-end;
  margin-top: 4px;
  font-size: var(--gh-font-2xs);
  color: var(--gh-text-muted);
}
